import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Player from '@vimeo/player';
import { supabase } from '../supabaseClient';
import { visibleModules } from '../lib/courseOutline';
import Navbar from '../components/Navbar';
import LockedLesson from '../components/LockedLesson';
import UserStats from '../components/UserStats';
import XPToast from '../components/XPToast';
import LessonDescription from '../components/LessonDescription';
import ModuleResources from '../components/ModuleResources';
import { loadModuleResources } from '../lib/moduleResources';
import { XP_PER_LESSON } from '../lib/xp';
import LessonCheck from '../components/lecke/LessonCheck';

const LevelUpOverlay = ({ level, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', gap: 16,
    }}
    onClick={onClose}
    >
      <div style={{
        fontSize: 64, animation: 'spin 0.5s ease-out',
      }}>⚡</div>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 14, color: '#00D2C8', fontWeight: 600, letterSpacing: '0.1em', marginBottom: 8 }}>
          SZINTLÉPÉS!
        </div>
        <div style={{ fontSize: 48, fontWeight: 800, color: '#fff', lineHeight: 1 }}>
          Szint {level}
        </div>
        <div style={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', marginTop: 8 }}>
          Gratulálunk! Folytasd így!
        </div>
      </div>
      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', marginTop: 8 }}>
        Kattints a bezáráshoz
      </div>
      <style>{`
        @keyframes spin {
          from { transform: scale(0) rotate(-180deg); opacity: 0; }
          to { transform: scale(1) rotate(0deg); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

const LessonPage = ({ session, access, profile }) => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  // Zárt lecke (Plusz 2. kör): a lesson_meta metaadata, tartalom nélkül
  const [locked, setLocked] = useState(null);
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [expandedModules, setExpandedModules] = useState([]);
  const [completedIds, setCompletedIds] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showXpToast, setShowXpToast] = useState(false);
  const [chapters, setChapters] = useState([]);
  const [savedPosition, setSavedPosition] = useState(null);
  const [playbackRate, setPlaybackRate] = useState(() => {
    const saved = parseFloat(localStorage.getItem('vimeo_playback_rate'));
    return [0.75, 1, 1.25, 1.5, 2].includes(saved) ? saved : 1;
  });
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const completedRef = useRef(false);
  const [levelUp, setLevelUp] = useState(null);
  const [bookmarked, setBookmarked] = useState(false);
  const [note, setNote] = useState('');
  const [noteSaved, setNoteSaved] = useState(false);
  const [noteTimeout, setNoteTimeout] = useState(null);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [moduleDone, setModuleDone] = useState(null);
  const [lessonResources, setLessonResources] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);

    const { data: lessonData } = await supabase
      .from('lessons').select('*').eq('id', lessonId).maybeSingle();
    if (!lessonData) {
      // Nem olvasható: ha a kurzus látható és a lecke publikált, a zárt nézet
      // jön (cím, modul, szint, gomb), nem hibaoldal; különben vissza a listához.
      const { data: meta } = await supabase.rpc('lesson_meta', { p_lesson: lessonId });
      const m = Array.isArray(meta) ? meta[0] : meta;
      if (m && !m.can_read) { setLocked(m); setLoading(false); return; }
      navigate('/tanfolyamok'); return;
    }
    setLocked(null);
    setLesson(lessonData);

    // Modul + tanfolyam info a modules táblából
    const { data: moduleData } = await supabase
      .from('modules')
      .select('*, courses(*)')
      .eq('id', lessonData.module_id)
      .single();
    setCourse(moduleData?.courses || null);
    // A modul segédletei a lecke alatt (Plusz-segédletek 1. kör).
    loadModuleResources(moduleData?.course_id).then(m => setLessonResources(m[lessonData.module_id] || []));

    // Összes modul a sidebar-hoz
    const { data: moduleRows } = await supabase
      .from('modules')
      .select('*')
      .eq('course_id', moduleData.course_id)
      .order('order_index');

    const loadedModules = await Promise.all(
      (moduleRows || []).map(async (module) => {
        const { data: lessons } = await supabase
          .from('lessons')
          .select('*')
          .eq('module_id', module.id)
          .order('order_index');
        return { ...module, lessons: lessons || [] };
      })
    );
    // Hidden lessons and modules left without any are not shown (courseOutline.js).
    const modulesWithLessons = visibleModules(loadedModules);
    setModules(modulesWithLessons);

    // Az aktív lecke modulját nyissuk ki alapból
    const activeModule = modulesWithLessons.find(m =>
      m.lessons.some(l => l.id === lessonId)
    );
    if (activeModule) setExpandedModules([activeModule.id]);

    // Összes haladás (sidebar + jelenlegi lecke)
    const { data: progressData } = await supabase
      .from('user_progress')
      .select('lesson_id')
      .eq('user_id', session.user.id)
      .not('completed_at', 'is', null);
    const ids = (progressData || []).map(p => p.lesson_id);
    setCompletedIds(ids);
    setCompleted(ids.includes(lessonId));

    // Load bookmark
    const { data: bm } = await supabase
      .from('lesson_bookmarks')
      .select('id')
      .eq('user_id', session.user.id)
      .eq('lesson_id', lessonId)
      .maybeSingle();
    setBookmarked(!!bm);

    // Load note
    const { data: noteData } = await supabase
      .from('lesson_notes')
      .select('content')
      .eq('user_id', session.user.id)
      .eq('lesson_id', lessonId)
      .maybeSingle();
    setNote(noteData?.content || '');

    // Load rating
    const { data: ratingData } = await supabase
      .from('lesson_ratings')
      .select('rating')
      .eq('user_id', session.user.id)
      .eq('lesson_id', lessonId)
      .maybeSingle();
    setRating(ratingData?.rating || 0);
    setHoverRating(0);

    setLoading(false);
  }, [lessonId, session.user.id, navigate]);

  useEffect(() => { load(); }, [load]);

  // SEO: védett oldal — ne indexelje a Google
  useEffect(() => {
    const robots = document.querySelector('meta[name="robots"]');
    const prevRobots = robots?.getAttribute('content');
    const prevTitle = document.title;
    if (robots) robots.setAttribute('content', 'noindex, nofollow');
    return () => {
      if (robots) robots.setAttribute('content', prevRobots || 'index, follow');
      document.title = prevTitle;
    };
  }, []);

  useEffect(() => {
    if (lesson?.title) document.title = `${lesson.title} — Web3 Kurzus Egyetem`;
  }, [lesson]);

  // Mobile breakpoint tracking (768px)
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Modul-befejezés overlay: 3 mp után automatikus továbblépés
  useEffect(() => {
    if (!moduleDone) return;
    const t = setTimeout(() => {
      navigate(`/lecke/${moduleDone.nextLessonId}`);
      setModuleDone(null);
    }, 3000);
    return () => clearTimeout(t);
  }, [moduleDone, navigate]);

  const toggleModule = (moduleId) => {
    setExpandedModules(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const toggleBookmark = async () => {
    if (bookmarked) {
      await supabase.from('lesson_bookmarks')
        .delete()
        .eq('user_id', session.user.id)
        .eq('lesson_id', lessonId);
      setBookmarked(false);
    } else {
      await supabase.from('lesson_bookmarks')
        .insert({ user_id: session.user.id, lesson_id: lessonId });
      setBookmarked(true);
    }
  };

  const submitRating = async (value) => {
    setRating(value);
    await supabase.from('lesson_ratings').upsert({
      user_id: session.user.id,
      lesson_id: lessonId,
      rating: value,
    }, { onConflict: 'user_id,lesson_id' });
  };

  const changeRate = (rate) => {
    setPlaybackRate(rate);
    localStorage.setItem('vimeo_playback_rate', String(rate));
    playerRef.current?.setPlaybackRate(rate).catch(() => {});
  };

  const handleNoteChange = (value) => {
    setNote(value);
    setNoteSaved(false);
    if (noteTimeout) clearTimeout(noteTimeout);
    const t = setTimeout(async () => {
      await supabase.from('lesson_notes').upsert({
        user_id: session.user.id,
        lesson_id: lessonId,
        content: value,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'user_id,lesson_id' });
      setNoteSaved(true);
    }, 1500);
    setNoteTimeout(t);
  };

  const postCourseCompletionToDiscord = async (courseName) => {
    const username = profile?.username || session?.user?.email?.split('@')[0] || 'Valaki';
    const webhook = process.env.REACT_APP_DISCORD_WEBHOOK_URL;
    if (!webhook) return;

    const messages = [
      `🎉 **${username}** teljesítette a **${courseName}** tanfolyamot! Gratulálunk!`,
      `🏆 **${username}** végzett a **${courseName}** tanfolyammal! Szép munka!`,
      `⚡ **${username}** megcsinálta a **${courseName}** tanfolyamot! 🚀`,
    ];
    const msg = messages[Math.floor(Math.random() * messages.length)];

    try {
      await fetch(webhook, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: msg }),
      });
    } catch (e) {}
  };

  const markComplete = async () => {
    if (completedRef.current) return;
    completedRef.current = true;

    await supabase.from('user_progress').upsert({
      user_id: session.user.id,
      lesson_id: lessonId,
      completed_at: new Date().toISOString(),
      xp_earned: XP_PER_LESSON,
    }, { onConflict: 'user_id,lesson_id' });

    // Update XP and streak
    const today = new Date().toISOString().split('T')[0];
    const { data: prof } = await supabase
      .from('profiles')
      .select('total_xp, streak_days, last_activity_date')
      .eq('id', session.user.id)
      .single();

    const lastDate = prof?.last_activity_date;
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    const newStreak = lastDate === today
      ? (prof?.streak_days || 1)
      : lastDate === yesterday
      ? (prof?.streak_days || 0) + 1
      : 1;

    const newXp = (prof?.total_xp || 0) + XP_PER_LESSON;

    await supabase.from('profiles').update({
      total_xp: newXp,
      streak_days: newStreak,
      last_activity_date: today,
    }).eq('id', session.user.id);

    // Level up check
    const oldLevel = Math.floor((prof?.total_xp || 0) / 200) + 1;
    const newLevel = Math.floor(newXp / 200) + 1;
    if (newLevel > oldLevel) {
      setLevelUp(newLevel);
    }

    setCompleted(true);
    setCompletedIds(prev => prev.includes(lessonId) ? prev : [...prev, lessonId]);
    setShowXpToast(true);

    // Discord notification when the whole course is completed
    const newCompletedIds = [...completedIds, lessonId];
    const allLessonIds = modules.flatMap(m => m.lessons).map(l => l.id);
    const allDone = allLessonIds.every(id => newCompletedIds.includes(id));
    if (allDone && course?.title) {
      postCourseCompletionToDiscord(course.title);
    }
  };

  // Keep a ref in sync so the Vimeo timeupdate handler reads fresh completion state
  useEffect(() => { completedRef.current = completed; }, [completed]);

  const vimeoMatch = lesson?.vimeo_url
    ? lesson.vimeo_url.match(/vimeo\.com\/(\d+)/)
    : null;
  // Unlisted/private videók privacy hash-t igényelnek. Elsőként a dedikált
  // vimeo_hash oszlopot használjuk, fallbackként az URL-be ágyazott hash-t
  // (vimeo.com/{id}/{hash}).
  const vimeoHash = lesson?.vimeo_hash
    || lesson?.vimeo_url?.match(/vimeo\.com\/\d+\/([a-zA-Z0-9]+)/)?.[1]
    || null;

  // Vimeo Player API: chapters, resume position, autosave, auto-complete
  useEffect(() => {
    if (!vimeoMatch || !iframeRef.current) return;

    const player = new Player(iframeRef.current);
    playerRef.current = player;

    // Mentett lejátszási sebesség alkalmazása (következő leckénél is megmarad)
    player.setPlaybackRate(playbackRate).catch(() => {});

    // Feature 1: Load chapters
    player.getChapters().then(ch => {
      if (ch && ch.length > 0) setChapters(ch);
    }).catch(() => {});

    // Feature 2: Restore saved position
    const loadPosition = async () => {
      const { data } = await supabase
        .from('user_progress')
        .select('playback_position')
        .eq('user_id', session.user.id)
        .eq('lesson_id', lessonId)
        .maybeSingle();
      if (data?.playback_position && data.playback_position > 10) {
        setSavedPosition(data.playback_position);
      }
    };
    loadPosition();

    // Feature 3: Save position every 10 seconds
    let saveInterval;
    player.on('play', () => {
      saveInterval = setInterval(async () => {
        const currentTime = await player.getCurrentTime();
        const duration = await player.getDuration();
        if (!currentTime || !duration) return;

        await supabase.from('user_progress').upsert({
          user_id: session.user.id,
          lesson_id: lessonId,
          playback_position: Math.floor(currentTime),
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id,lesson_id' });
      }, 10000);
    });

    player.on('pause', () => clearInterval(saveInterval));

    // Feature 4: Auto-complete at 80%
    player.on('timeupdate', async ({ percent }) => {
      if (percent >= 0.8 && !completedRef.current) {
        await markComplete();
      }
    });

    return () => {
      clearInterval(saveInterval);
      player.off('timeupdate');
      player.off('play');
      player.off('pause');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson, iframeRef.current]);

  if (locked) {
    return <LockedLesson meta={locked} access={access} session={session} isMobile={isMobile} />;
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0f1117' }}>
        <Navbar access={access} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
          <p style={{ color: 'rgba(255,255,255,0.4)' }}>Betöltés...</p>
        </div>
      </div>
    );
  }

  const allLessons = modules.flatMap(m => m.lessons);
  const currentIdx = allLessons.findIndex(l => l.id === lessonId);
  const prevLesson = allLessons[currentIdx - 1];
  const nextLesson = allLessons[currentIdx + 1];

  const currentModule = modules.find(m => m.lessons.some(l => l.id === lessonId));
  const nextModule = nextLesson ? modules.find(m => m.lessons.some(l => l.id === nextLesson.id)) : null;
  const crossingModule = !!(currentModule && nextModule && currentModule.id !== nextModule.id);

  return (
    <>
    {levelUp && <LevelUpOverlay level={levelUp} onClose={() => setLevelUp(null)} />}
    {moduleDone && (
      <div
        onClick={() => { navigate(`/lecke/${moduleDone.nextLessonId}`); setModuleDone(null); }}
        style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 14, padding: 24, textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 56, animation: 'mcPop 0.5s ease-out' }}>✅</div>
        <div style={{ color: '#fff', fontSize: 24, fontWeight: 800, lineHeight: 1.2 }}>
          {moduleDone.current.title} modul kész!
        </div>
        <div style={{
          color: '#fbbf24', fontWeight: 700, fontSize: 20,
          animation: 'mcPulse 0.8s ease-in-out infinite',
        }}>
          +{moduleDone.current.lessons.length * XP_PER_LESSON} XP
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); navigate(`/lecke/${moduleDone.nextLessonId}`); setModuleDone(null); }}
          style={{
            marginTop: 8,
            background: 'linear-gradient(135deg, #00D2C8, #8C50FF)',
            color: '#fff', border: 'none', borderRadius: 10,
            padding: '13px 26px', fontWeight: 700, fontSize: 15, cursor: 'pointer',
          }}
        >
          Következő modul: {moduleDone.next.title} →
        </button>
        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, marginTop: 4 }}>
          3 másodperc múlva automatikusan tovább…
        </div>
        <style>{`
          @keyframes mcPop {
            from { transform: scale(0) rotate(-15deg); opacity: 0; }
            to { transform: scale(1) rotate(0deg); opacity: 1; }
          }
          @keyframes mcPulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.12); }
          }
        `}</style>
      </div>
    )}
    <div style={{ minHeight: '100vh', background: '#0f1117' }}>
      <Navbar access={access} />

      {/* Mobile hamburger */}
      {isMobile && (
        <div style={{
          padding: '10px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          background: '#0a0a0f',
        }}>
          <button
            onClick={() => setSidebarOpen(true)}
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: 'rgba(255,255,255,0.8)',
              borderRadius: 8, padding: '8px 14px',
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}
          >
            ☰ Modulok
          </button>
        </div>
      )}

      <div style={isMobile
        ? { display: 'block' }
        : { display: 'flex', height: 'calc(100vh - 60px)' }
      }>
        {/* Sidebar */}
        <aside style={isMobile
          ? {
              position: 'fixed', top: 0, left: 0, bottom: 0,
              width: '100%', zIndex: 1000,
              background: '#0a0a0f',
              borderRight: '1px solid rgba(255,255,255,0.06)',
              overflowY: 'auto', padding: '16px 0',
              transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
              transition: 'transform 0.25s ease',
            }
          : {
              width: '280px', minWidth: '280px',
              background: '#0a0a0f',
              borderRight: '1px solid rgba(255,255,255,0.06)',
              overflowY: 'auto', padding: '16px 0',
            }
        }>
          {isMobile && (
            <div style={{ padding: '0 16px 12px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setSidebarOpen(false)}
                style={{
                  background: 'none', border: 'none',
                  color: 'rgba(255,255,255,0.6)', cursor: 'pointer',
                  fontSize: 15, fontWeight: 600,
                }}
              >
                ✕ Bezárás
              </button>
            </div>
          )}
          <UserStats profile={profile} session={session} />
          <div style={{ padding: '0 12px 16px' }}>
            <button
              onClick={() => course && navigate(`/tanfolyam/${course.slug}`)}
              style={{
                background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)',
                cursor: 'pointer', fontSize: '13px', padding: '0',
                marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px',
              }}
            >
              ← {course?.title || 'Vissza'}
            </button>
          </div>

          <div style={{ padding: '0 8px' }}>
            {modules.map(module => (
              <div key={module.id} style={{ marginBottom: '4px' }}>
                {/* Module header */}
                <div
                  onClick={() => toggleModule(module.id)}
                  style={{
                    padding: '10px 12px',
                    color: 'white', fontWeight: 700,
                    fontSize: '13px',
                    background: 'rgba(255,255,255,0.04)',
                    borderRadius: '8px', marginBottom: '2px',
                    cursor: 'pointer',
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ flex: 1, marginRight: '8px' }}>
                    {module.displayIndex}. {module.title}
                  </span>
                  <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', whiteSpace: 'nowrap' }}>
                    {module.lessons.filter(l => completedIds.includes(l.id)).length}/{module.lessons.length}
                    {' '}{expandedModules.includes(module.id) ? '▲' : '▼'}
                  </span>
                </div>

                {/* Lessons */}
                {expandedModules.includes(module.id) && module.lessons.map(l => {
                  const isCompleted = completedIds.includes(l.id);
                  const isActive = l.id === lessonId;
                  return (
                    <div
                      key={l.id}
                      onClick={() => { navigate(`/lecke/${l.id}`); setSidebarOpen(false); }}
                      style={{
                        padding: '7px 12px 7px 28px',
                        cursor: 'pointer', fontSize: '12px',
                        color: isActive ? 'white' : isCompleted ? '#00D2C8' : 'rgba(255,255,255,0.55)',
                        background: isActive ? 'rgba(0,210,200,0.08)' : 'transparent',
                        borderRadius: '6px',
                        borderLeft: isActive ? '2px solid #00D2C8' : '2px solid transparent',
                        display: 'flex', alignItems: 'center', gap: '8px',
                      }}
                    >
                      <span style={{ fontSize: '11px', flexShrink: 0 }}>
                        {isCompleted ? '✓' : '○'}
                      </span>
                      <span style={{ flex: 1, lineHeight: 1.4 }}>{l.title}</span>
                      {l.duration_minutes && (
                        <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '11px', marginLeft: 'auto', flexShrink: 0 }}>
                          {l.duration_minutes}p
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </aside>

        {/* Main content */}
        <main style={isMobile
          ? { width: '100%', overflowY: 'visible' }
          : { flex: 1, overflowY: 'auto' }
        }>
          <div style={{ maxWidth: '860px', margin: '0 auto', padding: isMobile ? '16px' : '32px 40px' }}>
            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 6 }}>
                <h1 style={{ color: 'white', fontSize: '22px', fontWeight: 700, margin: 0, flex: 1, marginRight: 12 }}>
                  {lesson.title}
                </h1>
                <button
                  onClick={toggleBookmark}
                  title={bookmarked ? 'Eltávolítás a kedvencekből' : 'Hozzáadás a kedvencekhez'}
                  style={{
                    background: bookmarked ? 'rgba(220,50,100,0.12)' : 'rgba(255,255,255,0.05)',
                    border: bookmarked ? '1px solid rgba(220,50,100,0.3)' : '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 8, padding: '8px 12px',
                    cursor: 'pointer', fontSize: 18, flexShrink: 0,
                    transition: 'all 0.15s',
                  }}
                >
                  {bookmarked ? '❤️' : '🤍'}
                </button>
              </div>
              {lesson.duration_minutes && (
                <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>
                  ⏱ {lesson.duration_minutes} perc
                </span>
              )}
            </div>

            {/* Vimeo embed */}
            {vimeoMatch && (
              <>
                {/* Resume position banner */}
                {savedPosition && (
                  <div style={{
                    background: 'rgba(140,80,255,0.08)',
                    border: '1px solid rgba(140,80,255,0.2)',
                    borderRadius: 8, padding: '10px 16px',
                    marginBottom: 12,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    fontSize: 13,
                  }}>
                    <span style={{ color: 'rgba(255,255,255,0.6)' }}>
                      ↩ Legutóbb itt jártál: {Math.floor(savedPosition / 60)}:{String(savedPosition % 60).padStart(2, '0')}
                    </span>
                    <button
                      onClick={() => {
                        if (playerRef.current) {
                          playerRef.current.setCurrentTime(savedPosition);
                          setSavedPosition(null);
                        }
                      }}
                      style={{
                        background: 'rgba(140,80,255,0.2)', border: '1px solid rgba(140,80,255,0.3)',
                        color: '#A97BFF', borderRadius: 6, padding: '4px 12px',
                        cursor: 'pointer', fontSize: 12, fontWeight: 600,
                      }}
                    >
                      Folytatás →
                    </button>
                  </div>
                )}

                <div style={{
                  position: 'relative', paddingBottom: '56.25%',
                  height: 0, overflow: 'hidden', borderRadius: '12px',
                  marginBottom: '24px',
                }}>
                  <iframe
                    ref={iframeRef}
                    src={`https://player.vimeo.com/video/${vimeoMatch[1]}${vimeoHash ? `?h=${vimeoHash}` : ''}`}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    frameBorder="0"
                    allowFullScreen
                    title={lesson.title}
                  />
                </div>

                {/* Playback speed */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
                  <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>Sebesség:</span>
                  {[0.75, 1, 1.25, 1.5, 2].map(rate => {
                    const active = playbackRate === rate;
                    return (
                      <button
                        key={rate}
                        onClick={() => changeRate(rate)}
                        style={{
                          padding: '5px 12px', borderRadius: 20, cursor: 'pointer',
                          fontSize: 12, fontWeight: 600,
                          border: active ? 'none' : '1px solid rgba(255,255,255,0.12)',
                          background: active ? '#00D2C8' : 'rgba(255,255,255,0.05)',
                          color: active ? '#07070b' : 'rgba(255,255,255,0.6)',
                          transition: 'all 0.15s',
                        }}
                      >
                        {rate}x
                      </button>
                    );
                  })}
                </div>

                {/* Chapters */}
                {chapters.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Fejezetek
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {chapters.map((ch, i) => (
                        <button
                          key={i}
                          onClick={() => playerRef.current?.setCurrentTime(ch.startTime)}
                          style={{
                            background: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            color: 'rgba(255,255,255,0.6)',
                            borderRadius: 6, padding: '5px 12px',
                            cursor: 'pointer', fontSize: 12,
                            display: 'flex', alignItems: 'center', gap: 6,
                          }}
                        >
                          <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>
                            {Math.floor(ch.startTime / 60)}:{String(Math.floor(ch.startTime % 60)).padStart(2, '0')}
                          </span>
                          {ch.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Description */}
            {lesson.description && (
              <div style={{
                color: 'rgba(255,255,255,0.7)', fontSize: '15px', lineHeight: 1.7,
                marginBottom: '24px',
              }}>
                <LessonDescription text={lesson.description} />
              </div>
            )}

            {/* Ellenőrző kérdések (Tananyag 1. kör): a leckekvíz, ha van; semmit nem zár le */}
            <LessonCheck lessonId={lesson.id} userId={session?.user?.id} />

            <ModuleResources items={lessonResources} session={session} variant="box" hely="egyetem_lecke" />

            {/* Kép: a lessons táblában nincs image_url, a kép a thumbnail_url; csak
                videó nélküli leckénél (videónál a lejátszó mutatja). */}
            {!vimeoMatch && lesson.thumbnail_url && (
              <img
                src={lesson.thumbnail_url}
                alt=""
                style={{ width: '100%', borderRadius: '12px', marginBottom: '24px' }}
              />
            )}

            {/* Mark complete */}
            <div style={{
              background: '#1a1d2e',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '12px', padding: '20px',
              display: 'flex', alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '24px',
              flexWrap: 'wrap', gap: '12px',
            }}>
              <div>
                <p style={{ color: 'white', margin: '0 0 2px', fontWeight: 600, fontSize: '14px' }}>
                  {completed ? '✓ Lecke teljesítve' : 'Teljesítetted a leckét?'}
                </p>
                <p style={{ color: 'rgba(255,255,255,0.4)', margin: 0, fontSize: '12px' }}>
                  {completed ? 'Szép munka! Lépj a következőre.' : 'Jelöld teljesítettnek a haladás követéséhez.'}
                </p>
              </div>
              {!completed && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: 'rgba(251,191,36,0.07)',
                  border: '0.5px solid rgba(251,191,36,0.2)',
                  borderRadius: 8, padding: '8px 12px', marginBottom: 12,
                  fontSize: 12, color: 'rgba(251,191,36,0.8)',
                }}>
                  ⚡ +20 XP a lecke teljesítéséért
                </div>
              )}
              <button
                onClick={markComplete}
                disabled={completed}
                style={{
                  background: completed ? 'rgba(0,210,200,0.1)' : 'linear-gradient(135deg, #00D2C8, #8C50FF)',
                  color: completed ? '#00D2C8' : '#fff',
                  border: completed ? '1px solid rgba(0,210,200,0.3)' : 'none',
                  borderRadius: 10, padding: '13px 28px',
                  fontWeight: 700, fontSize: 15,
                  cursor: completed ? 'default' : 'pointer',
                  transition: 'opacity 0.2s',
                }}
              >
                {completed ? '✓ Teljesítve' : '✓ Teljesítettem'}
              </button>
            </div>

            {/* Lesson rating */}
            {completed && (
              <div style={{
                background: 'rgba(140,80,255,0.05)',
                border: '1px solid rgba(140,80,255,0.18)',
                borderRadius: 12, padding: '16px 20px',
                marginBottom: 24,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexWrap: 'wrap', gap: 12,
              }}>
                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, fontWeight: 600 }}>
                  {rating > 0 ? 'Köszönjük a visszajelzést! ⭐' : 'Értékeld ezt a leckét'}
                </span>
                <div style={{ display: 'flex', gap: 4 }} onMouseLeave={() => setHoverRating(0)}>
                  {[1, 2, 3, 4, 5].map(star => {
                    const active = star <= (hoverRating || rating);
                    return (
                      <button
                        key={star}
                        onClick={() => submitRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        aria-label={`${star} csillag`}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          fontSize: 26, lineHeight: 1, padding: '0 2px',
                          color: active ? '#00D2C8' : 'rgba(255,255,255,0.2)',
                          transition: 'color 0.12s, transform 0.12s',
                          transform: hoverRating === star ? 'scale(1.18)' : 'scale(1)',
                          textShadow: active ? '0 0 8px rgba(0,210,200,0.45)' : 'none',
                        }}
                      >
                        ★
                      </button>
                    );
                  })}
                </div>
              </div>
            )}


            {/* Notes */}
            <div style={{ marginTop: 24, marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>
                  Saját jegyzetek
                </p>
                {noteSaved && (
                  <span style={{ fontSize: 11, color: '#1D9E75' }}>✓ Mentve</span>
                )}
              </div>
              <textarea
                value={note}
                onChange={e => handleNoteChange(e.target.value)}
                placeholder="Írj ide saját jegyzeteket ehhez a leckéhez..."
                rows={4}
                style={{
                  width: '100%', background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 8, padding: '12px 14px',
                  color: 'rgba(255,255,255,0.8)', fontSize: 14,
                  lineHeight: 1.6, resize: 'vertical',
                  outline: 'none', boxSizing: 'border-box',
                  fontFamily: 'inherit',
                }}
                onFocus={e => e.target.style.borderColor = 'rgba(0,210,200,0.3)'}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
              />
            </div>

            {/* Prev / Next navigation */}
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              marginTop: '40px', gap: '12px',
            }}>
              <button
                onClick={() => prevLesson && navigate(`/lecke/${prevLesson.id}`)}
                disabled={!prevLesson}
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: prevLesson ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.2)',
                  borderRadius: '8px', padding: '10px 20px',
                  cursor: prevLesson ? 'pointer' : 'default',
                  fontSize: '14px',
                  width: isMobile ? '100%' : 'auto',
                }}
              >
                ← Előző lecke
              </button>

              <button
                onClick={() => {
                  if (!nextLesson) return;
                  if (crossingModule) {
                    setModuleDone({ current: currentModule, next: nextModule, nextLessonId: nextLesson.id });
                  } else {
                    navigate(`/lecke/${nextLesson.id}`);
                  }
                }}
                disabled={!nextLesson}
                style={{
                  background: nextLesson ? '#00D2C8' : 'rgba(255,255,255,0.06)',
                  border: 'none',
                  color: nextLesson ? '#07070b' : 'rgba(255,255,255,0.2)',
                  borderRadius: '8px', padding: '10px 20px',
                  cursor: nextLesson ? 'pointer' : 'default',
                  fontSize: '14px', fontWeight: nextLesson ? 700 : 400,
                  width: isMobile ? '100%' : 'auto',
                }}
              >
                Következő lecke →
              </button>
            </div>
          </div>
        </main>
      </div>

      <XPToast show={showXpToast} xp={XP_PER_LESSON} />
    </div>
    </>
  );
};

export default LessonPage;
